import { z } from 'zod';
import { BAR_PAYMENT_METHODS, BAR_SALE_STATUSES, CREDENTIAL_TYPES, PAYMENT_METHODS, STOCK_MOVEMENT_KINDS, STOCK_WRITE_OFF_REASONS } from '@bulk/types';
import { dateOnlySchema, idSchema, paginationQuerySchema, publicAssetSchema } from './common';

const localizedText = z.record(z.string(), z.string());

export const barCatalogItemSchema = z.object({
  id: idSchema,
  name: localizedText,
  priceMinor: z.number().int(),
  photo: publicAssetSchema.nullable(),
  barcode: z.string().nullable().describe('A scanned barcode adds the item to the order'),
  stock: z.object({ quantity: z.number().int(), low: z.boolean() }).nullable().describe("This branch's shelf, for items the bar counts"),
});
export type BarCatalogItemDto = z.infer<typeof barCatalogItemSchema>;

export const barCatalogSchema = z.object({
  currency: z.string(),
  categories: z.array(z.object({ id: idSchema, name: localizedText, items: z.array(barCatalogItemSchema) })),
});
export type BarCatalogDto = z.infer<typeof barCatalogSchema>;

export const barCardSchema = z.object({
  credentialType: z.enum(CREDENTIAL_TYPES).default('RFID_CARD'),
  value: z.string().trim().min(1).max(200),
});
export type BarCardInput = z.infer<typeof barCardSchema>;

export const barCustomerSchema = z.object({
  memberId: idSchema,
  name: z.string(),
  memberNumber: z.string(),
  balanceMinor: z.number().int(),
  currency: z.string(),
});
export type BarCustomerDto = z.infer<typeof barCustomerSchema>;

export const createBarSaleSchema = z
  .object({
    lines: z
      .array(z.object({ itemId: idSchema, quantity: z.number().int().min(1).max(99) }))
      .min(1)
      .max(50),
    method: z.enum(BAR_PAYMENT_METHODS),
    card: barCardSchema.optional().describe('The tapped card: required to charge the balance, optional to put a cash or card sale on a member'),
    expectedTotalMinor: z.number().int().optional().describe('What the till showed; a different server total (a price changed meanwhile) is refused with 409 PRICE_CHANGED'),
  })
  .refine((v) => v.method !== 'WALLET' || v.card !== undefined, { message: 'Charging the balance needs the member to tap their card', path: ['card'] });
export type CreateBarSaleInput = z.infer<typeof createBarSaleSchema>;

export const barSaleLineSchema = z.object({
  id: idSchema,
  menuItemId: idSchema.nullable(),
  name: localizedText,
  unitPriceMinor: z.number().int(),
  quantity: z.number().int(),
  refundedQuantity: z.number().int(),
  lineTotalMinor: z.number().int(),
});
export type BarSaleLineDto = z.infer<typeof barSaleLineSchema>;

export const barRefundSchema = z.object({
  id: idSchema,
  method: z.enum(BAR_PAYMENT_METHODS),
  amountMinor: z.number().int(),
  reason: z.string(),
  refundedByName: z.string().nullable(),
  createdAt: z.string(),
  lines: z.array(z.object({ lineId: idSchema, quantity: z.number().int(), amountMinor: z.number().int() })),
});
export type BarRefundDto = z.infer<typeof barRefundSchema>;

export const barSaleSchema = z.object({
  id: idSchema,
  receiptNumber: z.string(),
  locationId: idSchema,
  locationName: z.string(),
  memberId: idSchema.nullable(),
  memberName: z.string().nullable(),
  method: z.enum(BAR_PAYMENT_METHODS),
  totalMinor: z.number().int(),
  refundedMinor: z.number().int(),
  currency: z.string(),
  status: z.enum(BAR_SALE_STATUSES),
  soldByName: z.string().nullable(),
  createdAt: z.string(),
  lines: z.array(barSaleLineSchema),
  refunds: z.array(barRefundSchema),
  balanceAfterMinor: z.number().int().nullable().describe("The member's balance right after a balance sale or refund; null otherwise"),
});
export type BarSaleDto = z.infer<typeof barSaleSchema>;

export const refundBarSaleSchema = z.object({
  lines: z
    .array(z.object({ lineId: idSchema, quantity: z.number().int().min(1).max(99) }))
    .min(1)
    .max(50)
    .optional()
    .describe('What goes back; everything not refunded yet when omitted'),
  reason: z.string().trim().min(3).max(500),
  card: barCardSchema.optional().describe("A balance sale goes back to the balance only when the buyer taps their card again"),
  restock: z.boolean().default(true).describe('Put counted items back on the shelf; off for something spilled or opened'),
});
export type RefundBarSaleInput = z.infer<typeof refundBarSaleSchema>;

export const barSalesQuerySchema = paginationQuerySchema.extend({
  date: dateOnlySchema.optional().describe("A local day in the gym's time zone; today when omitted"),
  memberId: idSchema.optional(),
});
export type BarSalesQuery = z.infer<typeof barSalesQuerySchema>;

export const STOCK_STATUSES = ['OK', 'LOW', 'OUT', 'NEGATIVE'] as const;
export type StockStatus = (typeof STOCK_STATUSES)[number];

export const stockBranchLevelSchema = z.object({
  locationId: idSchema,
  locationName: z.string(),
  quantity: z.number().int(),
  lowStockThreshold: z.number().int().nullable(),
  status: z.enum(STOCK_STATUSES),
});
export type StockBranchLevelDto = z.infer<typeof stockBranchLevelSchema>;

export const stockLevelSchema = z.object({
  itemId: idSchema,
  name: localizedText,
  categoryId: idSchema,
  categoryName: localizedText,
  barcode: z.string().nullable(),
  photo: publicAssetSchema.nullable(),
  sellable: z.boolean(),
  priceMinor: z.number().int().describe('The sale price on the menu'),
  quantity: z.number().int().describe('On the shelf; summed over the branches in view'),
  lowStockThreshold: z.number().int().nullable().describe("The branch's threshold; null across several branches"),
  low: z.boolean().describe('At or below the threshold'),
  status: z.enum(STOCK_STATUSES).describe('NEGATIVE below zero, OUT at zero, LOW at or below the threshold; across several branches the worst of them'),
  lastCostMinor: z.number().int().nullable().describe('The unit cost of the latest delivery that had one'),
  valueAtCostMinor: z.number().int().nullable().describe('What is on the shelf (above zero) at the last cost'),
  valueAtPriceMinor: z.number().int().describe('What is on the shelf (above zero) at the sale price'),
  lastDeliveryAt: z.string().nullable(),
  lastCountAt: z.string().nullable(),
  lastSaleAt: z.string().nullable(),
  soldLast30Days: z.number().int().describe('Units sold in the last 30 days, less units refunded'),
  daysOfCover: z.number().nullable().describe('On the shelf divided by the average sold per day over 30 days; null with no sales'),
  branches: z.array(stockBranchLevelSchema),
});
export type StockLevelDto = z.infer<typeof stockLevelSchema>;

export const stockLevelsSchema = z.object({
  locationId: idSchema.nullable().describe('The branch in view; null when several are summed'),
  locationName: z.string().nullable(),
  items: z.array(stockLevelSchema),
});
export type StockLevelsDto = z.infer<typeof stockLevelsSchema>;

export const stockDeliverySchema = z.object({
  lines: z
    .array(z.object({ itemId: idSchema, quantity: z.number().int().min(1).max(100_000), unitCostMinor: z.number().int().min(0).max(10_000_000).optional().describe('What one unit cost, for margins and stock value') }))
    .min(1)
    .max(100),
  note: z.string().trim().max(500).optional(),
});
export type StockDeliveryInput = z.infer<typeof stockDeliverySchema>;

export const stockCountSchema = z.object({
  lines: z
    .array(z.object({ itemId: idSchema, counted: z.number().int().min(0).max(100_000) }))
    .min(1)
    .max(200),
  note: z.string().trim().max(500).optional(),
});
export type StockCountInput = z.infer<typeof stockCountSchema>;

export const stockWriteOffSchema = z
  .object({
    itemId: idSchema,
    quantity: z.number().int().min(1).max(100_000),
    reasonCode: z.enum(STOCK_WRITE_OFF_REASONS),
    reason: z.string().trim().max(500).optional().describe('A note; required when the reason is OTHER'),
  })
  .refine((v) => v.reasonCode !== 'OTHER' || (v.reason?.length ?? 0) >= 3, { path: ['reason'], message: 'Say what happened' });
export type StockWriteOffInput = z.infer<typeof stockWriteOffSchema>;

export const stockThresholdSchema = z.object({
  lowStockThreshold: z.number().int().min(0).max(100_000).nullable(),
});
export type StockThresholdInput = z.infer<typeof stockThresholdSchema>;

export const stockMovementSchema = z.object({
  id: idSchema,
  itemId: idSchema,
  itemName: localizedText,
  kind: z.enum(STOCK_MOVEMENT_KINDS),
  quantity: z.number().int(),
  quantityAfter: z.number().int(),
  unitCostMinor: z.number().int().nullable(),
  reasonCode: z.enum(STOCK_WRITE_OFF_REASONS).nullable().describe('Why stock was written off; null for other movements and for write-offs made before reasons existed'),
  locationId: idSchema,
  locationName: z.string(),
  receiptNumber: z.string().nullable(),
  note: z.string().nullable(),
  userName: z.string().nullable(),
  createdAt: z.string(),
});
export type StockMovementDto = z.infer<typeof stockMovementSchema>;

export const stockMovementsQuerySchema = paginationQuerySchema.extend({
  itemId: idSchema.optional(),
});
export type StockMovementsQuery = z.infer<typeof stockMovementsQuerySchema>;

export const barReportQuerySchema = z.object({
  from: dateOnlySchema.describe("First local day, in the gym's time zone"),
  to: dateOnlySchema.describe('Last local day, included'),
});
export type BarReportQuery = z.infer<typeof barReportQuerySchema>;

const barBreakdownSchema = z.object({ key: z.string(), label: z.string(), salesMinor: z.number().int(), refundsMinor: z.number().int(), count: z.number().int() });

export const barTakingsSchema = z.object({ method: z.enum(BAR_PAYMENT_METHODS), salesMinor: z.number().int(), refundsMinor: z.number().int(), count: z.number().int() });
export type BarTakingsDto = z.infer<typeof barTakingsSchema>;

export const barReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  timezone: z.string(),
  currency: z.string(),
  branchName: z.string().nullable().describe('The branch the report is for; null for every branch the reader may see'),
  totals: z.object({
    salesMinor: z.number().int(),
    refundsMinor: z.number().int(),
    netMinor: z.number().int(),
    saleCount: z.number().int(),
    itemsSold: z.number().int(),
  }),
  byMethod: z.array(barTakingsSchema),
  byDay: z.array(z.object({ date: z.string(), salesMinor: z.number().int(), refundsMinor: z.number().int(), netMinor: z.number().int() })),
  byProduct: z.array(z.object({ key: z.string(), name: localizedText, quantity: z.number().int(), salesMinor: z.number().int() })),
  byCategory: z.array(z.object({ key: z.string(), name: localizedText.nullable().describe('Null for items deleted from the menu since'), quantity: z.number().int(), salesMinor: z.number().int() })),
  byStaff: z.array(barBreakdownSchema),
  byBranch: z.array(barBreakdownSchema),
  topUps: z.object({ totalMinor: z.number().int(), count: z.number().int(), byMethod: z.array(z.object({ method: z.enum(PAYMENT_METHODS), amountMinor: z.number().int(), count: z.number().int() })) }),
  balanceOutstandingMinor: z.number().int().describe('What members could still spend at the end of the period: money held for them, not revenue'),
});
export type BarReportDto = z.infer<typeof barReportSchema>;

export const barLossesSchema = z.object({
  from: z.string(),
  to: z.string(),
  currency: z.string(),
  branchName: z.string().nullable(),
  totals: z.object({
    netSalesMinor: z.number().int().describe("Bar sales less refunds in the period, without VAT when the gym charges it; shrink % is counted against this"),
    countedTimes: z.number().int().describe('Stock counts recorded in the period, one per item counted'),
    countLossUnits: z.number().int().describe('Units the counts found missing, net of units found extra; negative when counts found more than the system expected'),
    countLossAtCostMinor: z.number().int(),
    countLossAtPriceMinor: z.number().int(),
    writeOffUnits: z.number().int(),
    writeOffAtCostMinor: z.number().int(),
    writeOffAtPriceMinor: z.number().int(),
    unrestockedUnits: z.number().int().describe('Refunded units of counted items that were not put back on the shelf'),
    unrestockedAtCostMinor: z.number().int(),
    unrestockedAtPriceMinor: z.number().int(),
    shrinkAtCostMinor: z.number().int(),
    shrinkAtPriceMinor: z.number().int(),
    shrinkPct: z.number().nullable().describe('Shrink at price ÷ net sales × 100'),
    uncostedUnits: z.number().int().describe('Lost units with no cost on record; valued at price only'),
    cashOverMinor: z.number().int(),
    cashShortMinor: z.number().int().describe('Sum of the shifts that closed short, as a positive amount'),
  }),
  byItem: z.array(
    z.object({
      key: z.string(),
      name: localizedText,
      countDifference: z.number().int().describe('Net units the counts found: negative is missing'),
      countValueAtCostMinor: z.number().int(),
      writeOffUnits: z.number().int(),
      writeOffAtCostMinor: z.number().int(),
      unrestockedUnits: z.number().int(),
      shrinkAtCostMinor: z.number().int(),
      shrinkAtPriceMinor: z.number().int(),
    }),
  ),
  writeOffsByReason: z.array(
    z.object({
      reason: z.enum([...STOCK_WRITE_OFF_REASONS, 'UNSPECIFIED']).describe('UNSPECIFIED for write-offs made before reasons existed'),
      units: z.number().int(),
      atCostMinor: z.number().int(),
      atPriceMinor: z.number().int(),
    }),
  ),
  staff: z.array(
    z.object({
      userId: z.string().nullable(),
      name: z.string(),
      saleCount: z.number().int(),
      salesMinor: z.number().int(),
      refundCount: z.number().int(),
      refundsMinor: z.number().int(),
      cashRefundsMinor: z.number().int(),
      ownSaleRefunds: z.number().int().describe('Refunds this person made of their own sales'),
      refundRatePct: z.number().nullable().describe('Refunds they made ÷ sales they made × 100'),
      flagged: z.boolean().describe('Refund rate above twice the team median, with at least two refunds'),
    }),
  ),
  medianRefundRatePct: z.number().nullable(),
  shifts: z.array(
    z.object({
      id: idSchema,
      locationName: z.string(),
      openedAt: z.string(),
      closedAt: z.string(),
      openedByName: z.string().nullable(),
      closedByName: z.string().nullable(),
      expectedMinor: z.number().int(),
      countedMinor: z.number().int(),
      differenceMinor: z.number().int(),
    }),
  ),
});
export type BarLossesDto = z.infer<typeof barLossesSchema>;

export const AGING_BUCKETS = ['0-7', '8-14', '15-30', '31-60', '61+', 'NEVER'] as const;
export type AgingBucket = (typeof AGING_BUCKETS)[number];
export const SLOW_LINES = [14, 30, 60] as const;

export const barInsightsQuerySchema = barReportQuerySchema.extend({
  slowDays: z.coerce
    .number()
    .int()
    .refine((v) => (SLOW_LINES as readonly number[]).includes(v), 'Choose 14, 30 or 60')
    .default(30)
    .describe('An item not sold for more days than this is slow'),
});
export type BarInsightsQuery = z.infer<typeof barInsightsQuerySchema>;

export const barInsightsSchema = z.object({
  from: z.string(),
  to: z.string(),
  currency: z.string(),
  branchName: z.string().nullable(),
  slowDays: z.number().int(),
  reviewDays: z.number().int().describe('Days between orders the suggestion covers on top of the lead time'),
  abc: z.array(
    z.object({
      key: z.string(),
      name: localizedText,
      units: z.number().int(),
      revenueMinor: z.number().int(),
      sharePct: z.number(),
      cumulativePct: z.number(),
      grade: z.enum(['A', 'B', 'C']).describe('A: the items making the first 80% of revenue, B: the next 15%, C: the rest; graded on the share before the item'),
    }),
  ),
  aging: z.object({
    buckets: z.array(z.object({ bucket: z.enum(AGING_BUCKETS), items: z.number().int(), onHand: z.number().int(), atCostMinor: z.number().int() })),
    items: z.array(
      z.object({
        key: z.string(),
        name: localizedText,
        trackStock: z.boolean(),
        lastSoldAt: z.string().nullable(),
        daysSinceSale: z.number().int().nullable(),
        bucket: z.enum(AGING_BUCKETS),
        slow: z.boolean(),
        onHand: z.number().int().describe('Units on the shelves now, empty and negative shelves counted as none'),
        atCostMinor: z.number().int(),
      }),
    ),
  }),
  heatmap: z.object({
    cells: z.array(z.object({ weekday: z.number().int().min(1).max(7).describe('ISO: 1 is Monday'), hour: z.number().int().min(0).max(23), receipts: z.number(), units: z.number(), salesMinor: z.number().int().describe('Sale line totals, before refunds') })).describe('Averages per weekday of the period, in the gym time zone'),
    peak: z.object({ weekday: z.number().int(), hour: z.number().int() }).nullable(),
  }),
  stockouts: z.array(
    z.object({
      key: z.string(),
      name: localizedText,
      locationName: z.string(),
      days: z.array(z.string()).describe('Local days that closed with nothing on the shelf'),
      lostUnits: z.number().describe('What those days would have sold, from the same weekdays with stock'),
      lostSalesMinor: z.number().int(),
    }),
  ),
  suggestions: z.array(
    z.object({
      key: z.string(),
      name: localizedText,
      locationId: idSchema,
      locationName: z.string(),
      onHand: z.number().int(),
      dailyDemand: z.number().describe('Smoothed units a day over the days with stock'),
      leadTimeDays: z.number().int().nullable(),
      packSize: z.number().int().nullable(),
      safetyStock: z.number().int(),
      suggested: z.number().int().nullable().describe('Units to order now; null until the item has a lead time'),
    }),
  ),
});
export type BarInsightsDto = z.infer<typeof barInsightsSchema>;

export const barMembersQuerySchema = barReportQuerySchema.extend({
  dormantDays: z.coerce.number().int().min(30).max(365).default(90).describe('A balance with no wallet movement for more days than this is dormant'),
});
export type BarMembersQuery = z.infer<typeof barMembersQuerySchema>;

const segmentSchema = z.object({ key: z.string(), label: z.string().nullable().describe('The plan name; null for the fixed keys of age bands and genders'), buyers: z.number().int(), salesMinor: z.number().int(), spendPerBuyerMinor: z.number().int() });

export const barMembersSchema = z.object({
  from: z.string(),
  to: z.string(),
  currency: z.string(),
  branchName: z.string().nullable(),
  minGroupSize: z.number().int().describe('Segments with fewer buyers are left out, so nobody can be picked out'),
  dormantDays: z.number().int(),
  totals: z.object({
    visitingMembers: z.number().int().describe('Members let in at least once in the period'),
    buyingMembers: z.number().int().describe('Members with at least one bar receipt in the period'),
    penetrationPct: z.number().nullable().describe('Member-days with both a visit and a bar receipt ÷ member-days with a visit'),
    memberSalesMinor: z.number().int(),
    allSalesMinor: z.number().int(),
    attributedPct: z.number().nullable().describe('Share of bar sales that carry a member: balance sales and sales where the card was tapped'),
    spendPerBuyerMinor: z.number().int(),
    receiptsPerBuyer: z.number(),
  }),
  byDay: z.array(z.object({ date: z.string(), visitors: z.number().int(), buyers: z.number().int().describe('Visitors that day who also bought'), penetrationPct: z.number().nullable() })),
  frequency: z.array(z.object({ bucket: z.enum(['1', '2-3', '4-7', '8+']), buyers: z.number().int() })),
  recency: z.array(z.object({ bucket: z.enum(['0-7', '8-30', '31-90', '91+']), buyers: z.number().int() })).describe('Days since each buyer last bought, at the end of the period'),
  cohorts: z.array(z.object({ cohort: z.string().describe('YYYY-MM of the first bar purchase'), size: z.number().int(), returnedPct: z.array(z.number()).describe('Share of the cohort buying again in each following month, up to the current one') })),
  segments: z.object({ byPlan: z.array(segmentSchema), byAgeBand: z.array(segmentSchema), byGender: z.array(segmentSchema), hiddenGroups: z.number().int() }),
  dormant: z.object({
    count: z.number().int(),
    totalMinor: z.number().int(),
    members: z.array(z.object({ memberId: idSchema, name: z.string(), balanceMinor: z.number().int(), lastMovementAt: z.string() })),
  }),
});
export type BarMembersDto = z.infer<typeof barMembersSchema>;

export const MENU_QUADRANTS = ['STAR', 'PLOWHORSE', 'PUZZLE', 'DOG'] as const;
export type MenuQuadrant = (typeof MENU_QUADRANTS)[number];

const profitFigures = {
  units: z.number().int().describe('Units sold in the period and kept: sold minus refunded'),
  netSalesMinor: z.number().int().describe('What the kept units brought in, without VAT when the gym charges it'),
  costedSalesMinor: z.number().int().describe('The part of net sales whose lines carry a cost; margins are counted on this'),
  cogsMinor: z.number().int().describe('Cost of the kept units, at the moving-average cost each line was sold at'),
  grossProfitMinor: z.number().int().describe('Costed sales minus their cost'),
  marginPct: z.number().nullable().describe('Gross profit / costed sales × 100; null with no costed sales'),
};

export const barProfitabilitySchema = z.object({
  from: z.string(),
  to: z.string(),
  currency: z.string(),
  branchName: z.string().nullable(),
  vat: z.object({ registered: z.boolean(), ratePct: z.number() }),
  totals: z.object({
    grossSalesMinor: z.number().int().describe('Every line sold in the period at its price, VAT included'),
    refundsMinor: z.number().int().describe('What was refunded from those lines, whenever it was refunded'),
    vatMinor: z.number().int().describe('The VAT inside what customers kept; 0 when the gym does not charge VAT'),
    netSalesMinor: z.number().int(),
    uncostedSalesMinor: z.number().int().describe('Net sales of lines sold before the item had a cost; left out of margins'),
    costedSalesMinor: z.number().int(),
    cogsMinor: z.number().int(),
    writeOffsMinor: z.number().int().describe('Stock written off in the period, at the average cost when it was written off'),
    grossProfitMinor: z.number().int().describe('Costed sales minus their cost minus write-offs'),
    marginPct: z.number().nullable(),
    barCostPct: z.number().nullable().describe('Cost of goods / costed sales × 100'),
  }),
  byCategory: z.array(z.object({ key: z.string(), name: localizedText.nullable(), ...profitFigures })),
  byItem: z.array(
    z.object({
      key: z.string(),
      name: localizedText,
      categoryKey: z.string(),
      ...profitFigures,
      unitMarginMinor: z.number().int().nullable().describe('Gross profit per kept unit; null when the item has no cost'),
      popularityPct: z.number().describe("Share of its category's units"),
      quadrant: z.enum(MENU_QUADRANTS).nullable().describe('Menu-engineering class within its category; null when the item has no cost'),
    }),
  ),
  inventory: z.object({
    atCostMinor: z.number().int().describe('Units on the shelves now × their average cost; empty and negative shelves count nothing'),
    atRetailMinor: z.number().int().describe('Units on the shelves now × today’s price, without VAT when the gym charges it'),
    uncostedUnits: z.number().int().describe('Units on the shelves with no cost yet, left out of the cost value'),
    gmroi: z.number().nullable().describe("The period's gross profit per unit of currency held in stock at cost now"),
  }),
});
export type BarProfitabilityDto = z.infer<typeof barProfitabilitySchema>;

export const productPriceSchema = z.object({
  at: z.string(),
  priceMinor: z.number().int(),
  changedBy: z.string().nullable(),
});
export type ProductPriceDto = z.infer<typeof productPriceSchema>;

export const productOverviewSchema = z.object({
  itemId: idSchema,
  name: localizedText,
  categoryName: localizedText,
  barcode: z.string().nullable(),
  photo: publicAssetSchema.nullable(),
  priceMinor: z.number().int(),
  sellable: z.boolean(),
  trackStock: z.boolean(),
  stock: stockLevelSchema.nullable().describe('The shelves in view, for an item the bar counts'),
  prices: z.array(productPriceSchema).describe('Every sale price the item has had, oldest first; empty without bar.insights'),
});
export type ProductOverviewDto = z.infer<typeof productOverviewSchema>;

export const productInsightsQuerySchema = z.object({
  from: dateOnlySchema.describe("First local day, in the gym's time zone"),
  to: dateOnlySchema.describe('Last local day, included'),
});
export type ProductInsightsQuery = z.infer<typeof productInsightsQuerySchema>;

export const productDaySchema = z.object({
  date: z.string(),
  soldUnits: z.number().int(),
  refundedUnits: z.number().int(),
  revenueMinor: z.number().int().describe('Sales less refunds made that day'),
  receivedUnits: z.number().int(),
  writtenOffUnits: z.number().int(),
  countDifferenceUnits: z.number().int(),
  closingQuantity: z.number().int().nullable().describe('On the shelves at the end of the day; null before the item was ever counted'),
  priceMinor: z.number().int(),
  costMinor: z.number().int().nullable(),
});
export type ProductDayDto = z.infer<typeof productDaySchema>;

export const productInsightsSchema = z.object({
  from: z.string(),
  to: z.string(),
  locationId: idSchema.nullable(),
  totals: z.object({
    soldUnits: z.number().int(),
    refundedUnits: z.number().int(),
    revenueMinor: z.number().int(),
    receivedUnits: z.number().int(),
    writtenOffUnits: z.number().int(),
    countDifferenceUnits: z.number().int(),
    averagePerDay: z.number().describe('Units sold less refunded, per day of the period'),
    sellThrough: z.number().nullable().describe('Units sold less refunded, divided by units received; null with nothing received'),
    unitMarginMinor: z.number().int().nullable().describe('The sale price now less the last cost'),
  }),
  days: z.array(productDaySchema),
  hours: z.array(z.number().int()).length(24).describe("Units sold in each hour of the day, gym's time zone"),
  weekdayHours: z.array(z.array(z.number().int()).length(24)).length(7).describe('Units sold per weekday (Monday first) and hour'),
  weekdayHoursSalesMinor: z.array(z.array(z.number().int()).length(24)).length(7).describe('Sale line totals per weekday (Monday first) and hour, before refunds'),
});
export type ProductInsightsDto = z.infer<typeof productInsightsSchema>;
